import React, { useState } from 'react'
import {
    useLocation,
    useNavigate,
    useParams,
    useSearchParams,
} from 'react-router-dom'

import { Contract, ContractPosition, ContractPerformance } from '../../../types'

// import FooterLeftToolbarButtons from './Buttons/FooterLeftToolbarButtons'
import FooterRightToolbarButtons from './Buttons/FooterRightToolbarButtons'
import FooterLeftToolbarButtons from './Buttons/FooterLeftToolbarButtons'
import { performance } from 'perf_hooks'

type NavState = {
    from?: 'contract' | 'registry'
    contractRegNumber?: string
    fromStage?: string | null
}

type Props = {
    theme: string
    perfAction: boolean
    contractGuid: string | undefined
    performanceGuid: string | undefined
    performance: ContractPerformance

    user_guid: string | null
    pos_user_guid: string
    pos_supervisor_user_guid: string
    role: string

    stage: string
    status: string

    handleValidation: () => void
    isHaveError: boolean

    onPerformanceStatus: (performance_status: string) => void
    onPerformanceStage: (performance_stage: string) => void
}

const Footer: React.FC<Props> = (props) => {
    const location = useLocation()
    const navigate = useNavigate()
    const params = useParams()

    const navState = (location.state ?? {}) as NavState

    const [isSaving, setSaving] = useState<boolean>(false)
    const [savePerformancePopup, setSavePerformancePopup] =
        useState<boolean>(false)

    const [isDeleting, setDeleting] = useState<boolean>(false)
    const [deletePerformancePopup, setDeletePerformancePopup] =
        useState<boolean>(false)

    const [toEisSendPopup, setToEisSendPopup] = useState<boolean>(false)
    const [isToEisSending, setToEisSending] = useState<boolean>(false)

    const [toEisSendResponsePopup, setToEisSendResponsePopup] =
        useState<boolean>(false)
    const [toEisSendResponse, setToEisSendResponse] = useState<any | null>(null)

    const [updateFromEisPopup, setUpdateFromEisPopup] = useState<boolean>(false)
    const [isUpdatingFromEis, setUpdatingFromEis] = useState<boolean>(false)
    const [popupMessage, setPopupMessage] = useState('')
    const [forceUpdateUrl, setForceUpdateUrl] = useState<string | null>(null)

    const [updateErrorPopup, setUpdateErrorPopup] = useState<boolean>(false)
    const [updateErrorText, setUpdateErrorText] = useState<string>('')

    const handleClose = () => {
        // если пришли из реестра — вернемся туда же и на ту же стадию
        if (navState.from === 'registry') {
            navigate(`/hub/performances?stage=${props.stage}`, {
                replace: true,
            }) // <-- подставь свой реальный роут реестра
            return
        }

        // иначе (пришли из договора / нет state) — возвращаемся в договор
        const contractRegNumber =
            navState.contractRegNumber ?? params.contractRegNumber
        if (contractRegNumber) {
            navigate(
                `/hub/contract/${contractRegNumber}?tab=${encodeURIComponent('Исполнение договора')}`,
                { replace: true }
            )
            return
        }

        // совсем fallback
        navigate('/hub/contracts', { replace: true })
    }

    const savePerformance = async (): Promise<void> => {
        setSaving(true)
        const url =
            process.env.REACT_APP_API_URL +
            `/api/backend/v1/performance_contract/${props.performanceGuid}`

        try {
            const response = await fetch(url, {
                method: 'PUT',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(props.performance),
            })

            if (!response.ok) {
                throw new Error(
                    `Failed to create new position from. Status: ${response.status}`
                )
            }
            console.log('Position created successfully')
        } catch (error) {
            console.error('Error while creating position:', error)
            setSaving(false)
        } finally {
            setSaving(false)
        }
    }

    const deletePerformance = async (): Promise<void> => {
        setDeleting(true)
        const url =
            process.env.REACT_APP_API_URL +
            `/api/backend/v1/performance_contract/${props.performanceGuid}`

        try {
            const response = await fetch(url, {
                method: 'DELETE',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                },
            })

            if (!response.ok) {
                throw new Error(
                    `Failed to delete position. Status: ${response.status}`
                )
            }

            console.log('Position deleted successfully')
        } catch (error) {
            console.error('Error while deleting position:', error)
            setDeleting(false)
        } finally {
            setDeleting(false)
        }
    }

    const handleSave = () => {
        savePerformance()
    }

    const handleDeletePerformancePopup = () => {
        setDeletePerformancePopup(!deletePerformancePopup)
    }

    const handleDelete = () => {
        deletePerformance()
        handleClose()
    }

    const stageChangePerformance = async (
        guid: string,
        stagePerf: string
    ): Promise<void> => {
        const url =
            process.env.REACT_APP_API_URL +
            `/api/backend/v1/performance_change_stage/?performance_contract_guid=${guid}`

        try {
            const response = await fetch(url, {
                method: 'PUT',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ performance_stage: stagePerf }),
            })

            if (!response.ok) {
                throw new Error(
                    `Failed to change stage performance from. Status: ${response.status}`
                )
            }

            console.log('Performance stage change successfully')
        } catch (error) {
            console.error('Error while changing stage performance:', error)
        } finally {
        }
    }

    const statusChangePerformance = async (
        guid: string,
        statusPerf: string
    ): Promise<void> => {
        const url =
            process.env.REACT_APP_API_URL +
            `/api/backend/v1/performance_change_status/?performance_contract_guid=${guid}`

        try {
            const response = await fetch(url, {
                method: 'PUT',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ performance_status: statusPerf }),
            })

            if (!response.ok) {
                throw new Error(
                    `Failed to change status performance from. Status: ${response.status}`
                )
            }

            console.log('Performance status change successfully')
        } catch (error) {
            console.error('Error while changing status performance:', error)
        } finally {
        }
    }

    // Пример асинхронной функции для парсинга ответа от toEisSendPlan
    const parseEisSendResponse = async (response: any): Promise<string> => {
        // Получаем значение body из новой или старой вложенной структуры ответа
        const body =
            response?.message?.text?.body ?? response?.message?.text?.text?.body

        // Получаем значение result из вложенной структуры ответа
        const result = body?.result

        if (result === 'success') {
            props.onPerformanceStatus('Отправлено в ЕИС')
            statusChangePerformance(props.performance.guid, 'Отправлено в ЕИС')
            return 'Всё прошло успешно'
        } else if (result === 'failure') {
            const violations = body?.violations?.violation
            const errorDescriptions: string[] = []

            // Регулярное выражение для поиска GUID (регистр не важен)
            const guidRegex =
                /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi

            // Функция для обработки одного объекта нарушения
            const processViolation = async (v: any) => {
                if (v.level === 'error' && v.description) {
                    // Ищем GUID в тексте ошибки
                    const guidMatches = v.description.match(guidRegex)

                    if (guidMatches && guidMatches.length > 0) {
                        // Если найдено несколько GUID – для каждого формируем отдельное сообщение
                        for (const guid of guidMatches) {
                            try {
                                // Убираем GUID из исходного текста (чтобы не дублировать его)
                                const cleanedDescription = v.description
                                    .replace(guid, '')
                                    .trim()
                                // Формируем сообщение согласно требуемому формату
                                const formattedError = `${guid} ${cleanedDescription}`
                                errorDescriptions.push(formattedError)
                            } catch (err) {
                                console.error(
                                    'Ошибка при получении данных позиции для GUID:',
                                    guid,
                                    err
                                )
                                // Если ошибка при запросе, добавляем исходное описание
                                errorDescriptions.push(v.description)
                            }
                        }
                    } else {
                        // Если GUID не найден, просто добавляем исходное описание ошибки
                        errorDescriptions.push(v.description)
                    }
                }
            }

            // Если violations – это массив, обрабатываем каждый элемент
            if (Array.isArray(violations)) {
                for (const violation of violations) {
                    await processViolation(violation)
                }
            } else if (violations && violations.level === 'error') {
                // Если violation – это одиночный объект
                await processViolation(violations)
            }

            return errorDescriptions.join('\n\n')
        }

        // Обработка ошибок, которые вернул бэк через detail
        const detail = response?.detail

        if (detail) {
            // Если detail строка
            if (typeof detail === 'string') {
                return detail
            }

            // Если detail объект
            if (typeof detail === 'object') {
                const error = detail?.error
                const statusCode = detail?.status_code
                const detailText = detail?.detail

                if (statusCode === 502) {
                    return 'Ошибка при отправке исполнения в ЕИС:\n- Сервис интеграции ЕИС не отвечает.\n Пожалуйста, повторите попытку через 30 минут.'
                }

                if (statusCode === 503) {
                    return 'Ошибка при отправке исполнения в ЕИС:\n- Сервис интеграции ЕИС не отвечает.\n Пожалуйста, повторите попытку через 30 минут.'
                }

                if (statusCode === 504) {
                    return 'Ошибка при отправке исполнения в ЕИС:\n- Сервис интеграции ЕИС не ответил вовремя.\n Пожалуйста, повторите попытку.'
                }

                if (statusCode && detailText) {
                    return `Ошибка при отправке исполнения в ЕИС:\n- ${statusCode}: ${detailText}`
                }

                if (detailText) {
                    return `Ошибка при отправке исполнения в ЕИС:\n- ${detailText}`
                }

                if (error) {
                    return `Ошибка при отправке исполнения в ЕИС:\n- ${error}`
                }
            }
        }

        return 'Неизвестный формат ответа'
    }

    const toEisSendPerformance = async (perf_guid: string): Promise<void> => {
        const url =
            process.env.REACT_APP_API_URL +
            `/api/backend/v1/performance_contract_to_eis?performance_contract_guid=${perf_guid}`

        try {
            const response = await fetch(url, {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                },
            })

            let responseData: any = null

            try {
                responseData = await response.json()
            } catch (e) {
                responseData = null
            }

            if (!response.ok) {
                const parsedError = responseData
                    ? await parseEisSendResponse(responseData)
                    : `Ошибка при отправке исполнения в ЕИС:\n- HTTP ${response.status}`

                setToEisSendResponse(parsedError)
                setToEisSendResponsePopup(true)
                return
            }

            const parsedResponse = await parseEisSendResponse(responseData)

            setToEisSendResponse(parsedResponse)
            setToEisSendResponsePopup(true)
        } catch (error) {
            console.error('Error while sending contract to EIS:', error)
            setToEisSendResponse(
                `Ошибка при отправке исполнения в ЕИС:\n- ${String(error)}`
            )
            setToEisSendResponsePopup(true)
        } finally {
        }
    }

    const handleStageChange = (perf_guid: string, stage: string) => {
        stageChangePerformance(perf_guid, stage)
        props.onPerformanceStage(stage)
        // handleValidation()
    }

    const handleStatusChange = (perf_guid: string, status: string) => {
        if (status == 'Готово') {
            handleSave()
        }
        statusChangePerformance(perf_guid, status)
        props.onPerformanceStatus(status)
    }

    const handleToEisSendPopup = () => {
        savePerformance()
        setToEisSendPopup(!toEisSendPopup)
    }

    const handleToEisSendClick = async (perf_guid: string) => {
        setToEisSending(true)
        await toEisSendPerformance(perf_guid)
        setToEisSending(false)
        setToEisSendPopup(false)
    }

    const handleToEisSendResponsePopup = () => {
        setToEisSendResponsePopup(!toEisSendResponsePopup)
    }

    const handleUpdateFromEisPopup = () => {
        setUpdateFromEisPopup(!updateFromEisPopup)
        setPopupMessage('')
    }

    const updatePerformanceFromEis = async (
        perf_guid: string
    ): Promise<void> => {
        const url =
            process.env.REACT_APP_API_URL +
            `/api/backend/v1/performance_contract_from_eis?performance_contract_guid=${perf_guid}`

        setUpdatingFromEis(true)

        try {
            const response = await fetch(url, {
                method: 'PUT',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                },
            })

            // Успешный апдейт — просто переходим на страницу редактирования
            if (response.ok) {
                await response.json().catch(() => null)
                navigate(
                    `/hub/contract/${props.contractGuid}/performances/edit/${perf_guid}`
                )
                return
            }

            // --- обработка ошибок ---
            let errorData: any = null
            try {
                errorData = await response.json()
            } catch (e) {
                console.error('Failed to parse JSON from response:', e)
            }

            const detail: string =
                (errorData && (errorData.detail || errorData.message)) ||
                `Unexpected error. Status: ${response.status}`

            // 1) спец-кейс: уже обновлено
            if (detail.includes('уже обновлено')) {
                setPopupMessage(
                    `${detail}\nВыполнить обновление данных из ЕИС все равно?`
                )
                setForceUpdateUrl(
                    process.env.REACT_APP_API_URL +
                        `/api/backend/v1/performance_contract_from_eis?performance_contract_guid=${perf_guid}&force_update=true`
                )
                setUpdateFromEisPopup(true)
                return
            }

            // 2) любые другие ошибки — попап с textarea
            setUpdateErrorText(detail)
            setUpdateErrorPopup(true)
        } catch (error: any) {
            console.error('Error while updating performance from EIS:', error)
            const msg =
                error?.message ||
                'Неизвестная ошибка при обновлении данных из ЕИС'
            setUpdateErrorText(msg)
            setUpdateErrorPopup(true)
        } finally {
            setUpdatingFromEis(false)
        }
    }

    const handleForceUpdate = async () => {
        if (!forceUpdateUrl) return

        setUpdatingFromEis(true)
        try {
            const response = await fetch(forceUpdateUrl, {
                method: 'PUT',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                },
            })

            if (!response.ok) {
                let errorData: any = null
                try {
                    errorData = await response.json()
                } catch (e) {
                    console.error('Failed to parse JSON from response:', e)
                }

                const detail: string =
                    (errorData && (errorData.detail || errorData.message)) ||
                    `Failed to force update performance. Status: ${response.status}`

                setUpdateErrorText(detail)
                setUpdateErrorPopup(true)
                return
            }

            // Успешный принудительный апдейт
            navigate(
                `/hub/contract/${props.contractGuid}/performances/edit/${props.performance.guid}`
            )
        } catch (error: any) {
            console.error('Error while forcing update performance:', error)
            const msg =
                error?.message ||
                'Неизвестная ошибка при принудительном обновлении данных из ЕИС'
            setUpdateErrorText(msg)
            setUpdateErrorPopup(true)
        } finally {
            setUpdatingFromEis(false)
            setUpdateFromEisPopup(false)
        }
    }

    const handleUpdateErrorPopup = () => {
        const needReload = updateErrorText
            ?.toLowerCase()
            .includes('исполнение переведено')

        setUpdateErrorPopup(false)
        setUpdateErrorText('')

        if (needReload) {
            window.location.reload()
        }
    }

    return (
        <footer className="flex flex-row w-full min-h-10 items-center justify-end ">
            <div className="flex flex-row gap-2 p-2 px-[22px] w-full h-full bg-[#d2d5d8] dark:bg-[#262427] items-center justify-start">
                <FooterLeftToolbarButtons
                    user_guid={props.user_guid}
                    pos_user_guid={props.pos_user_guid}
                    pos_supervisor_user_guid={props.pos_supervisor_user_guid}
                    role={props.role}
                    stage={props.stage}
                    status={props.status}
                    perfAction={props.perfAction}
                    approve={false}
                    isHaveError={props.isHaveError}
                    theme={props.theme}
                    lastApprovalStatus={true}
                    lastApprovalStatusAccept={true}
                    organizationApproval={false}
                    isUpdatingFromEis={isUpdatingFromEis}
                    actions={{
                        return_to_edit: () => {
                            handleStageChange(
                                props.performance.guid,
                                'Редактирование'
                            )
                            handleStatusChange(
                                props.performance.guid,
                                'Черновик'
                            )
                        },
                        return_to_ready: () => {
                            handleStageChange(
                                props.performance.guid,
                                'Подготовка к размещению'
                            )
                            handleStatusChange(
                                props.performance.guid,
                                'Подготовка'
                            )
                        },
                        ready: () =>
                            handleStatusChange(
                                props.performance.guid,
                                'Готово'
                            ),
                        edit: () =>
                            handleStatusChange(
                                props.performance.guid,
                                'Черновик'
                            ),
                        edit_ready: () =>
                            handleStatusChange(
                                props.performance.guid,
                                'Подготовка'
                            ),
                        ready_to_sent: () => {
                            handleStageChange(
                                props.performance.guid,
                                'Подготовка к размещению'
                            )
                            handleStatusChange(
                                props.performance.guid,
                                'Подготовка'
                            )
                        },
                        ready_to_eis: () => {
                            handleStageChange(
                                props.performance.guid,
                                'Отправка в ЕИС'
                            )
                            handleStatusChange(
                                props.performance.guid,
                                'Готово к отправке'
                            )
                        },
                        send_to_eis: () => {
                            handleToEisSendPopup()
                        },
                        update: () =>
                            updatePerformanceFromEis(props.performance.guid),
                        validation: props.handleValidation,
                    }}
                />
                <div className="flex flex-1"></div>
                <FooterRightToolbarButtons
                    theme={props.theme}
                    perfAction={
                        !!(
                            props.perfAction ||
                            props.performance.performance_stage ===
                                'Отправка в ЕИС'
                        )
                    }
                    actions={{
                        save: handleSave,
                        delete: handleDeletePerformancePopup,
                        close: handleClose,
                    }}
                />
            </div>

            {toEisSendPopup && (
                <div className="fixed top-0 left-0 w-full h-full bg-[#000000] bg-opacity-50 flex justify-center items-center">
                    <div
                        className="p-5 rounded-lg bg-[#e5e5e5] dark:bg-[#262427] w-[450px] max-h-[600px]"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex flex-row gap-2 items-center justify-start">
                            <svg
                                id="info-circle"
                                className="w-5 h-5"
                                data-name="Layer 1"
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    fill="#FFBF00"
                                    d="M12,14a1.25,1.25,0,1,0,1.25,1.25A1.25,1.25,0,0,0,12,14Zm0-1.5a1,1,0,0,0,1-1v-3a1,1,0,0,0-2,0v3A1,1,0,0,0,12,12.5ZM12,2A10,10,0,1,0,22,12,10.01114,10.01114,0,0,0,12,2Zm0,18a8,8,0,1,1,8-8A8.00917,8.00917,0,0,1,12,20Z"
                                ></path>
                            </svg>
                            <h2 className="text-[#181818] dark:text-[#FFFFFF]">
                                Отправка исполнения договора в ЕИС
                            </h2>
                        </div>
                        <div className="flex flex-col mt-2 space-y-1">
                            <div className="flex flex-row gap-2 items-center justify-start">
                                <label className="text-[12.8px] text-[#181818] dark:text-[#FFFFFF]">
                                    Сведения об исполнении договора будут
                                    отправлены в ЕИС. Вы уверены, что хотите
                                    отправить сведения?
                                </label>
                            </div>
                        </div>
                        <div className="flex flex-row mt-5 text-right justify-end gap-3">
                            <button
                                className="flex flex-row min-w-[120px] h-6 px-4 rounded-[10px] justify-center items-center gap-2 text-xs shadow-md shadow-[#18181830] text-[#181818] dark:text-[#FFFFFF] bg-[#f6f6f6] dark:bg-[#494849] hover:bg-[#FFFFFF] dark:hover:bg-[#656365]"
                                onClick={handleToEisSendPopup}
                            >
                                Отмена
                            </button>
                            <button
                                className="flex px-4 py-1 min-w-[120px] h-6 rounded-[10px] items-center justify-center text-xs shadow-md shadow-[#18181830] text-[#181818] dark:text-[#FFFFFF] bg-[#83d2b7] dark:bg-[#187a33] hover:bg-[#8ce5c6] dark:hover:bg-[#1d9a40]"
                                onClick={() =>
                                    handleToEisSendClick(props.performance.guid)
                                }
                            >
                                {isToEisSending ? (
                                    <svg
                                        className="animate-spin h-4 w-4 text-[#181818] dark:text-[#FFFFFF]"
                                        xmlns="http://www.w3.org/2000/svg"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                    >
                                        <circle
                                            className="opacity-25"
                                            cx="12"
                                            cy="12"
                                            r="10"
                                            stroke="currentColor"
                                            strokeWidth="4"
                                        ></circle>
                                        <path
                                            className="opacity-75"
                                            fill="currentColor"
                                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                                        ></path>
                                    </svg>
                                ) : (
                                    'Отправить'
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {toEisSendResponsePopup && (
                <div className="fixed top-0 left-0 w-full h-full bg-[#000000] bg-opacity-50 flex justify-center items-center">
                    <div
                        className={`p-5 rounded-lg bg-[#e5e5e5] dark:bg-[#262427] ${
                            toEisSendResponse == 'Всё прошло успешно'
                                ? 'w-[450px]'
                                : 'w-[600px]'
                        }  h-fit`}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex flex-row w-full items-center justify-start gap-2">
                            {toEisSendResponse == 'Всё прошло успешно' ? (
                                <svg
                                    className="w-5 h-5"
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 24 24"
                                    id="times-circle"
                                >
                                    <path
                                        fill="#187A33"
                                        d="M14.72,8.79l-4.29,4.3L8.78,11.44a1,1,0,1,0-1.41,1.41l2.35,2.36a1,1,0,0,0,.71.29,1,1,0,0,0,.7-.29l5-5a1,1,0,0,0,0-1.42A1,1,0,0,0,14.72,8.79ZM12,2A10,10,0,1,0,22,12,10,10,0,0,0,12,2Zm0,18a8,8,0,1,1,8-8A8,8,0,0,1,12,20Z"
                                    ></path>
                                </svg>
                            ) : (
                                <svg
                                    className="w-5 h-5"
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 24 24"
                                    id="times-circle"
                                >
                                    <path
                                        fill="#C15C5C"
                                        d="M15.71,8.29a1,1,0,0,0-1.42,0L12,10.59,9.71,8.29A1,1,0,0,0,8.29,9.71L10.59,12l-2.3,2.29a1,1,0,0,0,0,1.42,1,1,0,0,0,1.42,0L12,13.41l2.29,2.3a1,1,0,0,0,1.42,0,1,1,0,0,0,0-1.42L13.41,12l2.3-2.29A1,1,0,0,0,15.71,8.29Zm3.36-3.36A10,10,0,1,0,4.93,19.07,10,10,0,1,0,19.07,4.93ZM17.66,17.66A8,8,0,1,1,20,12,7.95,7.95,0,0,1,17.66,17.66Z"
                                    ></path>
                                </svg>
                            )}

                            <h2 className="text-[#181818] dark:text-[#FFFFFF] text-[18px]">
                                {toEisSendResponse == 'Всё прошло успешно'
                                    ? 'Исполнение договора успешно отправлено'
                                    : 'Найдены ошибки при отправке'}
                            </h2>
                        </div>
                        <div className="flex flex-col mt-2 space-y-1">
                            {toEisSendResponse == 'Всё прошло успешно' ? (
                                <div className="flex flex-row gap-2 items-center justify-start">
                                    <label className="text-[12.8px] text-[#181818] dark:text-[#FFFFFF]">
                                        Для дальнейшей работы разместите
                                        исполнение договора в ЕИС и нажмите
                                        кнопку «Обновить из ЕИС»
                                    </label>
                                </div>
                            ) : (
                                <div className="flex flex-col mt-2 space-y-1">
                                    <div className="flex flex-row gap-2 items-center justify-start">
                                        <label className="text-[12.8px] text-[#181818] dark:text-[#FFFFFF]">
                                            Внимание! Для отправки исполнения в
                                            ЕИС необходимо исправить следующие
                                            ошибки:
                                        </label>
                                    </div>
                                    <div className="flex flex-col mt-2 space-y-1">
                                        <textarea
                                            className="form-textarea flex w-full h-full text-[11px] items-start justify-start p-2 rounded-md border-0"
                                            value={toEisSendResponse}
                                            rows={21}
                                            disabled
                                            readOnly
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className="flex flex-row mt-5 text-right justify-end">
                            <button
                                className="flex flex-row min-w-[120px] h-6 px-4 rounded-[10px] justify-center items-center gap-2 text-xs shadow-md shadow-[#18181830] text-[#181818] dark:text-[#FFFFFF] bg-[#f6f6f6] dark:bg-[#494849] hover:bg-[#FFFFFF] dark:hover:bg-[#656365]"
                                onClick={handleToEisSendResponsePopup}
                            >
                                Закрыть
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {updateFromEisPopup && (
                <div
                    className="fixed top-0 left-0 w-full h-full bg-[#000000] bg-opacity-50 flex justify-center items-center"
                    onClick={handleUpdateFromEisPopup}
                >
                    <div
                        className="p-5 rounded-lg bg-[#e5e5e5] dark:bg-[#262427] w-[450px] max-h-[600px]"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex flex-row gap-2 items-center justify-left">
                            <h2 className="text-[#181818] dark:text-[#FFFFFF]">
                                Обновление данных
                            </h2>
                        </div>
                        <div className="flex flex-col mt-2 space-y-1">
                            <div className="flex flex-row gap-2 items-center justify-left">
                                <div className="flex flex-row w-5 h-5 items-center justify-center text-[#181818] dark:text-[#FFFFFF]">
                                    <svg
                                        id="info-circle"
                                        className="w-5 h-5"
                                        data-name="Layer 1"
                                        xmlns="http://www.w3.org/2000/svg"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            fill="#FFBF00"
                                            d="M12,14a1.25,1.25,0,1,0,1.25,1.25A1.25,1.25,0,0,0,12,14Zm0-1.5a1,1,0,0,0,1-1v-3a1,1,0,0,0-2,0v3A1,1,0,0,0,12,12.5ZM12,2A10,10,0,1,0,22,12,10.01114,10.01114,0,0,0,12,2Zm0,18a8,8,0,1,1,8-8A8.00917,8.00917,0,0,1,12,20Z"
                                        ></path>
                                    </svg>
                                </div>
                                <label className="text-[12.8px] text-[#181818] dark:text-[#FFFFFF]">
                                    {popupMessage}
                                </label>
                            </div>
                        </div>
                        <div className="flex flex-row mt-5 text-right justify-end">
                            {isUpdatingFromEis ? (
                                ''
                            ) : (
                                <button
                                    className="flex flex-row min-w-[120px] h-6 px-4 rounded-[10px] justify-center items-center gap-2 text-xs shadow-md shadow-[#18181830] text-[#181818] dark:text-[#FFFFFF] bg-[#f6f6f6] dark:bg-[#494849] hover:bg-[#FFFFFF] dark:hover:bg-[#656365]"
                                    onClick={handleUpdateFromEisPopup}
                                >
                                    Отмена
                                </button>
                            )}
                            <button
                                className={`flex px-4 py-1 min-w-[120px] h-6 rounded-full shadow-md shadow-[#18181830] text-xs text-[#181818] dark:text-[#FFFFFF] ml-2 items-center justify-center ${!isUpdatingFromEis ? 'bg-[#83d2b7] hover:bg-[#8ce5c6] dark:bg-[#187A33] dark:hover:bg-[#1D9A40]' : 'bg-[#494849]'} `}
                                onClick={() => handleForceUpdate()} // Вызвать handleForceUpdate
                                disabled={isUpdatingFromEis ? true : false}
                            >
                                {isUpdatingFromEis ? (
                                    <svg
                                        className="animate-spin h-4 w-4 text-[#181818] dark:text-[#FFFFFF]"
                                        xmlns="http://www.w3.org/2000/svg"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                    >
                                        <circle
                                            className="opacity-25"
                                            cx="12"
                                            cy="12"
                                            r="10"
                                            stroke="currentColor"
                                            strokeWidth="4"
                                        ></circle>
                                        <path
                                            className="opacity-75"
                                            fill="currentColor"
                                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                                        ></path>
                                    </svg>
                                ) : (
                                    'Обновить'
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {updateErrorPopup && (
                <div
                    className="fixed top-0 left-0 w-full h-full bg-[#000000] bg-opacity-50 flex justify-center items-center z-50"
                    onClick={handleUpdateErrorPopup}
                >
                    <div
                        className="p-5 rounded-lg bg-[#e5e5e5] dark:bg-[#262427] w-[600px] h-fit"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex flex-row w-full items-center justify-start gap-2">
                            <svg
                                className="w-5 h-5"
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    fill="#C15C5C"
                                    d="M15.71,8.29a1,1,0,0,0-1.42,0L12,10.59,9.71,8.29A1,1,0,0,0,8.29,9.71L10.59,12l-2.3,2.29a1,1,0,0,0,0,1.42,1,1,0,0,0,1.42,0L12,13.41l2.29,2.3a1,1,0,0,0,1.42,0,1,1,0,0,0,0-1.42L13.41,12l2.3-2.29A1,1,0,0,0,15.71,8.29Zm3.36-3.36A10,10,0,1,0,4.93,19.07,10,10,0,1,0,19.07,4.93ZM17.66,17.66A8,8,0,1,1,20,12,7.95,7.95,0,0,1,17.66,17.66Z"
                                ></path>
                            </svg>
                            <h2 className="text-[#181818] dark:text-[#FFFFFF] text-[18px]">
                                Ошибки при обновлении данных из ЕИС
                            </h2>
                        </div>
                        <div className="flex flex-col mt-2 space-y-1 gap-2">
                            <div className="flex flex-col mt-2 space-y-1">
                                <label className="text-[14px] text-[#181818] dark:text-[#FFFFFF]">
                                    Внимание! При обновлении исполнения из ЕИС
                                    возникли ошибки:
                                </label>
                            </div>
                            <div className="flex flex-col mt-2 space-y-1">
                                <textarea
                                    className="form-textarea flex w-full h-full text-[11px] items-start justify-start p-2 rounded-md border-0"
                                    value={updateErrorText}
                                    rows={21}
                                    disabled
                                    readOnly
                                />
                            </div>
                        </div>
                        <div className="flex flex-row mt-5 text-right justify-end">
                            <button
                                className="flex flex-row min-w-[120px] h-6 px-4 rounded-[10px] justify-center items-center gap-2 text-xs shadow-md shadow-[#18181830] text-[#181818] dark:text-[#FFFFFF] bg-[#f6f6f6] dark:bg-[#494849] hover:bg-[#FFFFFF] dark:hover:bg-[#656365]"
                                onClick={handleUpdateErrorPopup}
                            >
                                Закрыть
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {deletePerformancePopup && (
                <div
                    className="fixed top-0 left-0 w-full h-full bg-[#000000] bg-opacity-50 flex justify-center items-center"
                    onClick={handleDeletePerformancePopup}
                >
                    <div
                        className="p-5 rounded-lg bg-[#e5e5e5] dark:bg-[#262427] w-fit max-h-[600px]"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex flex-col text-sm text-[#181818] dark:text-[#FFFFFF] items-center justify-center gap-5">
                            <div className="flex flex-col">
                                <label className="flex mb-1 text-sm text-[#181818] dark:text-[#FFFFFF]">
                                    Удалить исполнение?
                                </label>
                            </div>
                            <div className="flex w-full flex-row items-center justify-end gap-3">
                                <button
                                    className="flex flex-row min-w-[120px] h-6 px-4 rounded-[10px] justify-center items-center gap-2 text-xs shadow-md shadow-[#18181830] text-[#181818] dark:text-[#FFFFFF] bg-[#f6f6f6] dark:bg-[#494849] hover:bg-[#FFFFFF] dark:hover:bg-[#656365]"
                                    onClick={handleDeletePerformancePopup}
                                >
                                    Отмена
                                </button>
                                <button
                                    className="flex px-4 py-1 min-w-[120px] h-6 rounded-[10px] items-center justify-center text-xs shadow-md shadow-[#18181830] text-[#181818] bg-[#ed9cae] hover:bg-[#ffa7ba] dark:bg-[#701F1F] dark:hover:bg-[#9e2c2c] dark:text-[#FFFFFF]"
                                    onClick={handleDelete}
                                >
                                    Удалить
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </footer>
    )
}

export default Footer
